/**
 * Admin Automation Rules Store
 *
 * Manages deliverable automation rules for admin users
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { automationRulesService } from '@/services/automationRulesService';
import { logger } from '@/services/loggerService';

export const useAdminAutomationRulesStore = defineStore('adminAutomationRules', () => {
  //-------------------------------
  // STATE
  //-------------------------------

  /**
   * All rules indexed by ID
   * @type {Object}
   */
  const items = ref({});

  /**
   * Loading state
   * @type {Boolean}
   */
  const loading = ref(false);

  /**
   * Store initialization state
   * @type {Boolean}
   */
  const initialized = ref(false);

  /**
   * Error state
   * @type {String|null}
   */
  const error = ref(null);

  /**
   * Unsubscribe function for rules listener
   * @type {Function|null}
   */
  let rulesUnsubscribe = null;

  /**
   * Modal state for creating/editing rules
   * @type {Object}
   */
  const modalState = ref({
    isOpen: false,
    mode: 'create', // 'create' or 'edit'
    rule: null
  });

  /**
   * Currently selected rule for viewing execution history
   * @type {String|null}
   */
  const selectedRuleId = ref(null);

  /**
   * Execution history for the selected rule
   * @type {Array}
   */
  const executionHistory = ref([]);

  /**
   * Unsubscribe function for execution history listener
   * @type {Function|null}
   */
  let historyUnsubscribe = null;

  //-------------------------------
  // GETTERS
  //-------------------------------

  /**
   * Get rule by ID
   */
  const getRuleById = computed(() => (id) => {
    return items.value[id] || null;
  });

  /**
   * Get all rules as array, sorted by creation date (newest first)
   */
  const allRules = computed(() => {
    return Object.values(items.value).sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  });

  /**
   * Get enabled rules only
   */
  const enabledRules = computed(() => {
    return allRules.value.filter(rule => rule.enabled);
  });

  /**
   * Get disabled rules only
   */
  const disabledRules = computed(() => {
    return allRules.value.filter(rule => !rule.enabled);
  });

  /**
   * Get rules grouped by trigger type
   */
  const rulesByTriggerType = computed(() => {
    const grouped = {
      field_change: [],
      date_based: [],
      calendar_event: [],
      conversation: [],
      dormant: [],
      button_click: []
    };

    Object.values(items.value).forEach(rule => {
      const triggerType = rule.trigger?.type;
      if (triggerType && grouped[triggerType]) {
        grouped[triggerType].push(rule);
      }
    });

    return grouped;
  });

  /**
   * Get rules grouped by category
   * Returns array of { name, rules } sorted alphabetically, "Uncategorized" last
   */
  const rulesByCategory = computed(() => {
    const grouped = {};
    allRules.value.forEach(rule => {
      const cat = rule.category || '';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(rule);
    });

    const entries = Object.entries(grouped).map(([name, rules]) => ({ name, rules }));
    entries.sort((a, b) => {
      if (a.name === '') return 1;
      if (b.name === '') return -1;
      return a.name.localeCompare(b.name);
    });
    return entries;
  });

  /**
   * Get all unique non-empty category names
   */
  const allCategories = computed(() => {
    const cats = new Set();
    allRules.value.forEach(rule => {
      if (rule.category) cats.add(rule.category);
    });
    return [...cats].sort();
  });

  /**
   * Get enabled action button rules
   */
  const actionButtonRules = computed(() => {
    return enabledRules.value.filter(rule => rule.trigger?.type === 'button_click');
  });

  /**
   * Get count of rules by status
   */
  const ruleCounts = computed(() => ({
    total: allRules.value.length,
    enabled: enabledRules.value.length,
    disabled: disabledRules.value.length
  }));

  /**
   * Get total error count across all rules
   */
  const totalErrorCount = computed(() => {
    return Object.values(items.value).reduce((sum, rule) => {
      return sum + (rule.errorCount || 0);
    }, 0);
  });

  /**
   * Check if any rules have errors
   */
  const hasErrors = computed(() => totalErrorCount.value > 0);

  /**
   * Get rules that have errors
   */
  const rulesWithErrors = computed(() => {
    return allRules.value.filter(rule => (rule.errorCount || 0) > 0);
  });

  //-------------------------------
  // ACTIONS
  //-------------------------------

  /**
   * Initialize the store with real-time listener
   */
  async function initialize() {
    if (initialized.value) {
      logger.info('[AdminAutomationRules] Already initialized');
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      // Set up real-time listener
      rulesUnsubscribe = automationRulesService.subscribeToRules((rules) => {
        const rulesMap = {};
        rules.forEach(rule => {
          rulesMap[rule.id] = rule;
        });
        items.value = rulesMap;

        logger.info('[AdminAutomationRules] Rules updated', {
          count: rules.length
        });
      });

      initialized.value = true;
      logger.info('[AdminAutomationRules] Store initialized');
    } catch (err) {
      error.value = err.message;
      logger.error('[AdminAutomationRules] Failed to initialize', {
        error: err.message
      });
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create a new automation rule
   * @param {Object} ruleData - The rule data
   * @returns {Promise<{success: boolean, id?: string}>}
   */
  async function createRule(ruleData) {
    try {
      const result = await automationRulesService.createRule(ruleData);
      logger.info('[AdminAutomationRules] Rule created', {
        ruleId: result.id
      });
      return result;
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to create rule', {
        error: err.message
      });
      throw err;
    }
  }

  /**
   * Update an existing rule
   * @param {string} ruleId - The rule ID
   * @param {Object} updates - The updates to apply
   * @returns {Promise<{success: boolean}>}
   */
  async function updateRule(ruleId, updates) {
    try {
      const result = await automationRulesService.updateRule(ruleId, updates);
      logger.info('[AdminAutomationRules] Rule updated', { ruleId });
      return result;
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to update rule', {
        error: err.message,
        ruleId
      });
      throw err;
    }
  }

  /**
   * Toggle rule enabled state
   * @param {string} ruleId - The rule ID
   * @returns {Promise<{success: boolean}>}
   */
  async function toggleEnabled(ruleId) {
    const rule = items.value[ruleId];
    if (!rule) {
      throw new Error('Rule not found');
    }

    try {
      const newEnabled = !rule.enabled;
      const result = await automationRulesService.toggleRuleEnabled(ruleId, newEnabled);
      logger.info('[AdminAutomationRules] Rule toggled', {
        ruleId,
        enabled: newEnabled
      });
      return result;
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to toggle rule', {
        error: err.message,
        ruleId
      });
      throw err;
    }
  }

  /**
   * Delete a rule
   * @param {string} ruleId - The rule ID
   * @returns {Promise<{success: boolean}>}
   */
  async function deleteRule(ruleId) {
    try {
      const result = await automationRulesService.deleteRule(ruleId);
      logger.info('[AdminAutomationRules] Rule deleted', { ruleId });
      return result;
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to delete rule', {
        error: err.message,
        ruleId
      });
      throw err;
    }
  }

  /**
   * Select a rule and load its execution history
   * @param {string} ruleId - The rule ID to select
   */
  async function selectRule(ruleId) {
    // Clean up previous subscription
    if (historyUnsubscribe) {
      historyUnsubscribe();
      historyUnsubscribe = null;
    }

    selectedRuleId.value = ruleId;
    executionHistory.value = [];

    if (!ruleId) return;

    // Subscribe to execution history
    historyUnsubscribe = automationRulesService.subscribeToExecutionHistory(
      ruleId,
      (history) => {
        executionHistory.value = history;
      }
    );
  }

  /**
   * Get error logs for a specific rule
   * @param {string} ruleId - The rule ID
   * @returns {Promise<Array>}
   */
  async function getErrorLogs(ruleId) {
    try {
      return await automationRulesService.getErrorLogs(ruleId);
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to get error logs', {
        error: err.message,
        ruleId
      });
      throw err;
    }
  }

  /**
   * Clear error logs for a specific rule
   * @param {string} ruleId - The rule ID
   * @returns {Promise<{success: boolean}>}
   */
  async function clearErrors(ruleId) {
    try {
      const result = await automationRulesService.clearErrors(ruleId);
      logger.info('[AdminAutomationRules] Errors cleared', { ruleId });
      return result;
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to clear errors', {
        error: err.message,
        ruleId
      });
      throw err;
    }
  }

  /**
   * Execute an action button
   * @param {string} ruleId - The rule ID
   * @param {string} deliverableId - The deliverable ID
   * @returns {Promise<Object>} Result from the Cloud Function
   */
  async function executeActionButton(ruleId, deliverableId) {
    try {
      logger.info('[AdminAutomationRules] Executing action button', {
        ruleId,
        deliverableId
      });
      const result = await automationRulesService.executeActionButton(ruleId, deliverableId);
      logger.info('[AdminAutomationRules] Action button executed', {
        ruleId,
        deliverableId,
        success: result?.success
      });
      return result;
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to execute action button', {
        error: err.message,
        ruleId,
        deliverableId
      });
      throw err;
    }
  }

  /**
   * Bulk update category for multiple rules
   * @param {Array<string>} ruleIds - Array of rule IDs
   * @param {string} category - The category to assign
   */
  async function bulkUpdateCategory(ruleIds, category) {
    try {
      await automationRulesService.bulkUpdateCategory(ruleIds, category);
      logger.info('[AdminAutomationRules] Bulk category updated', {
        count: ruleIds.length,
        category
      });
    } catch (err) {
      logger.error('[AdminAutomationRules] Failed to bulk update category', {
        error: err.message
      });
      throw err;
    }
  }

  //-------------------------------
  // MODAL ACTIONS
  //-------------------------------

  /**
   * Open modal to create a new rule
   */
  function openCreateModal() {
    modalState.value = {
      isOpen: true,
      mode: 'create',
      rule: null
    };
  }

  /**
   * Open modal to edit an existing rule
   * @param {Object} rule - The rule to edit
   */
  function openEditModal(rule) {
    modalState.value = {
      isOpen: true,
      mode: 'edit',
      rule: { ...rule }
    };
  }

  /**
   * Close the modal
   */
  function closeModal() {
    modalState.value = {
      isOpen: false,
      mode: 'create',
      rule: null
    };
  }

  //-------------------------------
  // CLEANUP
  //-------------------------------

  /**
   * Reset the store state
   */
  function $reset() {
    if (rulesUnsubscribe) {
      rulesUnsubscribe();
      rulesUnsubscribe = null;
    }
    if (historyUnsubscribe) {
      historyUnsubscribe();
      historyUnsubscribe = null;
    }

    items.value = {};
    loading.value = false;
    initialized.value = false;
    error.value = null;
    modalState.value = {
      isOpen: false,
      mode: 'create',
      rule: null
    };
    selectedRuleId.value = null;
    executionHistory.value = [];

    logger.info('[AdminAutomationRules] Store reset');
  }

  return {
    // State
    items,
    loading,
    initialized,
    error,
    modalState,
    selectedRuleId,
    executionHistory,

    // Getters
    getRuleById,
    allRules,
    enabledRules,
    disabledRules,
    rulesByTriggerType,
    rulesByCategory,
    allCategories,
    actionButtonRules,
    ruleCounts,
    totalErrorCount,
    hasErrors,
    rulesWithErrors,

    // Actions
    initialize,
    createRule,
    updateRule,
    toggleEnabled,
    deleteRule,
    selectRule,
    getErrorLogs,
    clearErrors,
    executeActionButton,
    bulkUpdateCategory,

    // Modal Actions
    openCreateModal,
    openEditModal,
    closeModal,

    // Cleanup
    $reset
  };
});
